// Read-aloud delivery analysis for an AI Interview turn — extracted from
// interview.js into its own module so it's independently unit-testable
// (same pattern as pcmWorklet.js / resumeUpload.js in this directory).
//
// Detects a candidate reading an answer aloud (from a script/second device)
// instead of speaking spontaneously: fast, even, disfluency-free speech.
// Real extempore speech has fillers and variable pace. We flag only when
// several signals agree — report-only, a human reviews it.
export const READ_ALOUD = {
  WPM_HIGH: 170, // sustained words/min above this is faster than typical extempore speech
  WPM_VERY_HIGH: 200, // clear read-aloud territory
  DISFLUENCY_LOW: 0.015, // ≤1.5% filler words → suspiciously clean for a spoken answer
  MIN_WORDS: 25, // need enough words for WPM/disfluency to be meaningful
  LONG_THINK_MS: 20000, // >20s of silence before the first word = long pre-answer pause
  // The "fast" gate alone missed real reading delivered at a normal-to-brisk
  // pace: the real tell is METRONOMIC EVENNESS + near-zero disfluency, not
  // raw speed — someone reading a script doesn't have to read it fast. So a
  // second, independent path: moderate-or-higher pace + very even pacing +
  // slightly more tolerant disfluency also counts, even under WPM_HIGH.
  WPM_MODERATE: 130, // below this even perfect pacing is unremarkable (genuinely slow natural speakers exist)
  PACING_CV_LOW: 6, // coefficient of variation of inter-word gaps — below this is suspiciously metronomic
  DISFLUENCY_LOW_LOOSE: 0.025, // slightly more tolerant than DISFLUENCY_LOW for the pacing-based path
  // Pauses (gaps >= 0.8s where the candidate stopped speaking) per 100 words
  // at or below this is "suspiciously few" — reading a script rarely needs a
  // mid-answer pause since the words are already there. Matches the "native
  // fluency" tier used for the general fluency score server-side.
  PAUSE_RATE_SUSPICIOUS: 3,
}
export const DISFLUENCY_RE = /\b(um+|uh+|er+|ah+|hmm+|like|you know|i mean|basically|actually|sort of|kind of)\b/gi

// Pure analyzer: given the final transcript, Deepgram speech stats (real spoken
// duration), and time-to-first-word, returns the WPM/disfluency profile plus
// boolean verdicts.
export const analyzeAnswerDelivery = (answer, speechStats, timeToFirstWordMs, fallbackSpokenSec = 0) => {
  const text = (answer || '').trim()
  const words = text ? text.split(/\s+/).filter(Boolean) : []
  const wordCount = (speechStats && speechStats.wordCount) || words.length
  // Prefer Deepgram's real spoken duration (last word end - first word start);
  // it excludes think-time, so WPM reflects actual delivery speed. When the
  // live WS never yielded a `speech` summary for this turn (dropped mid-turn,
  // fell back to batch STT, etc.) fall back to first-word-to-submit instead of
  // silently giving up — a hard 0 here meant wpm was always 0 and a fast
  // reader could never be flagged just because the precise timing didn't
  // arrive, regardless of how fast they actually spoke.
  const spokenSec = speechStats && speechStats.spokenSec > 0 ? speechStats.spokenSec : fallbackSpokenSec
  const wpm = spokenSec > 0 ? Math.round((wordCount / spokenSec) * 60) : 0
  // Prefer Deepgram's own native filler-word count (from live filler_words=True,
  // see fastapi-ai-engine ai_interview.py _speech_stats) — more reliable than a
  // text regex, which misses variants and can be thrown off by smart-formatting.
  // Falls back to the regex when Deepgram telemetry isn't available (e.g. the
  // batch-STT fallback was used for this turn).
  const disfluenciesFromText = (text.match(DISFLUENCY_RE) || []).length
  const fillerCount = speechStats && speechStats.fillerCount != null ? speechStats.fillerCount : disfluenciesFromText
  const disfluencyRate = wordCount > 0 ? fillerCount / wordCount : 0
  // Pacing evenness (coefficient of variation of inter-word gaps) and average
  // per-word ASR confidence — computed server-side from Deepgram's raw word
  // timings/confidence, passed through unchanged. Reading aloud is metronomic
  // (low pacingCv) and clearly/evenly enunciated (high, consistent confidence);
  // spontaneous speech is bursty and more variable. null when unavailable
  // (e.g. batch-STT fallback, which doesn't compute these).
  const pacingCv = speechStats && speechStats.pacingCv != null ? speechStats.pacingCv : null
  const avgWordConfidence = speechStats && speechStats.avgWordConfidence != null ? speechStats.avgWordConfidence : null
  // Pauses (gaps >= 0.8s where the candidate stopped speaking) per 100 words —
  // computed server-side from the same word timings used for pacingCv (see
  // fastapi-ai-engine ai_interview.py _speech_stats). Reading a script rarely
  // pauses mid-answer since there's nothing to think about; genuine
  // spontaneous speech pauses to formulate thoughts. null when unavailable.
  const pauseRate = speechStats && speechStats.pauseRatePer100 != null ? speechStats.pauseRatePer100 : null
  const pauseCount = speechStats && speechStats.pauseCount != null ? speechStats.pauseCount : null
  // Words the recognizer heard with low confidence (< 0.75), the same
  // pronunciation proxy the Communication assessment uses. Passed straight
  // through from the Deepgram stats — this module derives nothing from them;
  // the server turns them into the report's speech-clarity score so old
  // interviews can be re-derived without a new frontend build. null/[] on the
  // Sarvam path, which exposes no per-word confidence.
  const lowConfidenceCount = speechStats && speechStats.lowConfidenceCount != null ? speechStats.lowConfidenceCount : null
  const lowConfidenceRatio = speechStats && speechStats.lowConfidenceRatio != null ? speechStats.lowConfidenceRatio : null
  const lowConfidenceWords = (speechStats && speechStats.lowConfidenceWords) || []
  const enoughWords = wordCount >= READ_ALOUD.MIN_WORDS
  const pauseOk = pauseRate == null || pauseRate <= READ_ALOUD.PAUSE_RATE_SUSPICIOUS

  // Read-aloud pace: EITHER fast + unnaturally clean (original path) OR
  // moderately-paced + suspiciously EVEN + clean (new path). Reading a script
  // doesn't require reading it fast — the tell is metronomic, disfluency-free
  // delivery, which can happen at any reasonable pace. Gating on speed alone
  // missed real read-aloud answers delivered at 140-165 wpm with near-zero
  // disfluency and very even pacing. Both paths also require a suspiciously
  // low pause rate when available — genuine spontaneous speech usually pauses
  // to formulate thoughts even when otherwise fast/clean/even.
  const fastAndClean = wpm >= READ_ALOUD.WPM_HIGH && disfluencyRate <= READ_ALOUD.DISFLUENCY_LOW && pauseOk
  const evenAndClean =
    wpm >= READ_ALOUD.WPM_MODERATE &&
    pacingCv != null &&
    pacingCv <= READ_ALOUD.PACING_CV_LOW &&
    disfluencyRate <= READ_ALOUD.DISFLUENCY_LOW_LOOSE &&
    pauseOk
  const readingPaceSuspected = enoughWords && (fastAndClean || evenAndClean)
  // Delayed-then-fluent: sat silent a long time, then produced a fast clean
  // answer — the long pause likely went to reading, not thinking.
  const delayedFluentAnswer =
    enoughWords &&
    timeToFirstWordMs >= READ_ALOUD.LONG_THINK_MS &&
    (fastAndClean || evenAndClean)

  return {
    wpm,
    wordCount,
    spokenSec,
    disfluencyRate: Math.round(disfluencyRate * 1000) / 1000,
    fillerCount,
    pacingCv,
    avgWordConfidence,
    pauseRate,
    pauseCount,
    lowConfidenceCount,
    lowConfidenceRatio,
    lowConfidenceWords,
    timeToFirstWordMs,
    readingPaceSuspected,
    delayedFluentAnswer,
    veryFast: wpm >= READ_ALOUD.WPM_VERY_HIGH,
  }
}
